import cn from 'classnames';
import { useHistory } from 'react-router';

import { DEFAULT_MINIMUM_PRECISION, VANILLA_SETTLING_ASSET } from 'constants/constants';
import { CURRENCY_TYPE } from 'constants/enums';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { formatFiat } from 'helpers/currency';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import {
  addZeroesUntilCorrectPrecision,
  afterDecimalLength,
  convertExponentialToDecimal,
  getFormattedChangeAmount,
  getProductUrlByContractType,
} from 'helpers/utils';
import subscribe from 'HOC/subscribe';
import { lastPriceChangeSelector, lastPriceSelector } from 'selectors/tickerSelectors';
import { productsSelector } from 'selectors/tradeSelectors';
import { useAppSelector } from 'storeHooks';
import assetDefaultFallbackImg from 'styles/assets/asset_default_fallback.png';
import { ContractType } from 'types/IProducts';
import Box from 'UIKit/Box';

import styles from './style.module.scss';
import { TopCoinsOverviewProps } from './types';

const LastPrice = subscribe(
  ({ symbol, tickSize }: { symbol: string; tickSize: string }) => {
    const lastPrice = lastPriceSelector({ symbol, tick_size: tickSize });
    const precision = tickSize ? afterDecimalLength(tickSize) : 0;
    const formattedPrice =
      Number(lastPrice) === 0
        ? lastPrice
        : addZeroesUntilCorrectPrecision(
            convertExponentialToDecimal(lastPrice),
            precision
          );

    return (
      (<span className={styles.price} data-palette="LastPrice">
        {formatFiat(CURRENCY_TYPE.USD, formattedPrice ?? '0', 'en', {
          useGrouping: false,
          maximumFractionDigits: precision,
          minimumFractionDigits: DEFAULT_MINIMUM_PRECISION,
        })}
      </span>)
    );
  }
);

const LastPriceChangeValue = subscribe(({ symbol }: { symbol: string }) => {
  const lastPriceChange = lastPriceChangeSelector({ symbol });
  const isPositiveChange = lastPriceChange ? Number(lastPriceChange) >= 0 : false;
  const priceChangeClassNames = cn(styles.priceChange, {
    [styles.negative]: !isPositiveChange,
  });

  return (
    (<span className={priceChangeClassNames} data-palette="LastPriceChangeValue">
      {getFormattedChangeAmount(Number(lastPriceChange))}
    </span>)
  );
});

const TopCoinsOverview = ({
  symbol = '',
  index = 0,
  showDefaultIcons = [],
  handleImageError = () => {},
}: TopCoinsOverviewProps) => {
  const history = useHistory();
  const products = useAppSelector(productsSelector);
  const findProductBySymbol = (
    productList: Record<string, any>,
    symbolToFind: string
  ) => {
    return Object.values(productList).find(product => product.symbol === symbolToFind);
  };
  const productData = findProductBySymbol(products, symbol) || {};
  const tickSize = productData?.tick_size;
  const underlyingAsset = symbol?.replace(VANILLA_SETTLING_ASSET, '') || '';
  const customSymbol = symbol || '';
  const redirectLink = getProductUrlByContractType(
    ContractType.PerpetualFutures,
    underlyingAsset,
    symbol
  );

  const handleRowClick = () => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.MARKETS.MARKETS_OVERVIEW_CONTRACT_CLICKED, {
      product_name: symbol,
      product_type: ContractType.PerpetualFutures,
    });
    history.push(redirectLink);
  };

  return (
    (<tbody key={symbol} data-palette="TopCoinsOverview">
      <tr
        data-testid={`${symbol}-market-card-row-click`}
        className={styles.rowItem}
        onClick={handleRowClick}
      >
        <td>
          <Box className={styles.assetSymbolContractNameWrapper}>
            <img
              data-testid={`${symbol}-fallback-icon`}
              className={styles.assetSymbol}
              src={
                showDefaultIcons[index]
                  ? assetDefaultFallbackImg
                  : `https://static.delta.exchange/common/icons/crypto-coins/${underlyingAsset.toLowerCase()}.png`
              }
              alt={underlyingAsset}
              onError={() => handleImageError(index)}
            />
            <Box
              as="span"
              className={styles.contractName}
              data-testid={`${customSymbol}-contract-name`}
            >
              {customSymbol}
            </Box>
          </Box>
        </td>
        <td>
          <Box className={styles.pricePriceChangeWrapper}>
            <LastPrice symbol={symbol} tickSize={tickSize} />
            <LastPriceChangeValue symbol={symbol} />
          </Box>
        </td>
      </tr>
    </tbody>)
  );
};

export default TopCoinsOverview;
